import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ChevronDown } from "lucide-react";
import * as Accordion from "@radix-ui/react-accordion";
import { MaskLines, Reveal } from "@/components/motion";
import { SectionLabel } from "@/components/shared";
import { FAQS } from "@/data/site";

export default function FAQ() {
  return (
    <div data-testid="faq-page">
      <section className="relative overflow-hidden pb-10 pt-40 md:pt-48">
        <div className="mx-auto max-w-[1400px] px-5 md:px-10">
          <SectionLabel>Questions & Answers</SectionLabel>
          <h1 className="font-heading text-6xl uppercase leading-[0.82] tracking-tight text-white sm:text-8xl lg:text-[8rem]">
            <MaskLines lines={["Got", <span className="text-neon-green">Questions?</span>]} />
          </h1>
          <Reveal delay={0.6}>
            <p className="mt-8 max-w-2xl font-body text-lg leading-relaxed text-white/70">
              Everything parents and players ask before joining ARD Midsouth Performance. Still unsure?{" "}
              <Link to="/contact" className="text-neon-green underline">Reach out</Link> — we're happy to help.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="relative z-10 bg-obsidian pb-24 md:pb-32">
        <div className="mx-auto max-w-3xl px-5 md:px-10">
          <Accordion.Root type="single" collapsible className="border-t border-white/10" data-testid="faq-accordion">
            {FAQS.map((item, i) => (
              <Reveal key={i} delay={Math.min(i * 0.04, 0.3)}>
                <Accordion.Item value={`item-${i}`} className="border-b border-white/10">
                  <Accordion.Header>
                    <Accordion.Trigger
                      data-testid={`faq-trigger-${i}`}
                      className="group flex w-full items-center justify-between gap-6 py-6 text-left [&[data-state=open]>span>svg]:rotate-180"
                    >
                      <span className="flex items-start gap-4">
                        <span className="font-heading text-lg text-neon-green/70">{String(i + 1).padStart(2, "0")}</span>
                        <span className="font-heading text-xl uppercase leading-tight tracking-wide text-white transition-colors group-hover:text-neon-green sm:text-2xl">
                          {item.q}
                        </span>
                      </span>
                      <span className="flex h-9 w-9 shrink-0 items-center justify-center border border-white/20 text-neon-green transition-colors group-hover:border-neon-green">
                        <ChevronDown size={18} strokeWidth={2.5} className="transition-transform duration-300" />
                      </span>
                    </Accordion.Trigger>
                  </Accordion.Header>
                  <Accordion.Content className="overflow-hidden data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down">
                    <p className="max-w-2xl pb-6 pl-9 font-body text-base leading-relaxed text-white/60">
                      {item.a}
                    </p>
                  </Accordion.Content>
                </Accordion.Item>
              </Reveal>
            ))}
          </Accordion.Root>

          <Reveal delay={0.1}>
            <div className="mt-16 flex flex-col items-start justify-between gap-6 border border-white/10 bg-surface p-8 sm:flex-row sm:items-center">
              <div>
                <h3 className="font-heading text-2xl uppercase text-white">Still Have Questions?</h3>
                <p className="mt-2 font-body text-sm text-white/60">Our coaching staff is a message away.</p>
              </div>
              <Link
                to="/contact"
                data-testid="faq-contact-btn"
                className="group flex shrink-0 items-center gap-2 bg-neon-green px-8 py-4 font-heading text-sm uppercase tracking-widest text-black"
              >
                Contact Us
                <ArrowUpRight size={18} strokeWidth={3} className="transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
